import { defineStore } from 'pinia'
import { ApiError, call, identity, refreshOnce, type Schemas } from '../api/http'

type Session = Schemas['Session']

/**
 * Auth + workspace state. The access token lives in memory only; the refresh token is an
 * HttpOnly cookie, so `boot()` restores the session (and its active workspace) after reload.
 */
export const useSession = defineStore('session', {
  state: () => ({
    token: null as string | null,
    user: null as Schemas['User'] | null,
    workspace: null as Schemas['Workspace'] | null,
    workspaces: [] as Schemas['Workspace'][],
    platformAdmin: false,
    ready: false,
  }),
  getters: {
    authenticated: (s) => s.token !== null,
    isBusiness: (s) => s.workspace?.kind === 'business',
    // role from the workspace object, which identity returns for the caller
    canAdmin: (s) => s.workspace?.role === 'owner' || s.workspace?.role === 'admin',
    // ledger writes need role ≥ member
    canWrite: (s) => !!s.workspace && s.workspace.role !== 'viewer',
  },
  actions: {
    apply(s: Session) {
      this.token = s.access_token
      this.user = s.user
      this.workspace = s.workspace
    },
    clear() {
      this.$reset()
      this.ready = true
    },
    /** Restore a session from the refresh cookie. Never throws. */
    async boot() {
      if (await refreshOnce()) await this.loadMe().catch(() => {})
      this.ready = true
    },
    /** Raw refresh, used as the http layer's refresh hook (callers use refreshOnce). */
    async refresh(): Promise<boolean> {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          this.apply(await call(identity.POST('/session/refresh')))
          return true
        } catch (e) {
          // Another tab rotated the cookie first; the browser now holds the new one.
          if (attempt === 0 && e instanceof ApiError && e.code === 'refresh_superseded') continue
          break
        }
      }
      this.$reset()
      return false
    },
    async loadMe() {
      const me = await call(identity.GET('/me'))
      this.user = me.user
      this.workspaces = me.workspaces
      this.platformAdmin = me.platform_admin
      this.workspace = me.workspaces.find((w) => w.id === me.active_workspace_id) ?? this.workspace
    },
    async login(email: string, password: string) {
      this.apply(await call(identity.POST('/session', { body: { email, password } })))
      await this.loadMe()
    },
    async register(body: Schemas['CreateAccountRequest']) {
      this.apply(await call(identity.POST('/accounts', { body })))
      await this.loadMe()
    },
    async switchWorkspace(workspaceId: string) {
      this.apply(await call(identity.POST('/session/workspace', { body: { workspace_id: workspaceId } })))
      await this.loadMe()
    },
    async acceptInvitation(token: string) {
      this.apply(await call(identity.POST('/invitations/{token}/accept', { params: { path: { token } } })))
      await this.loadMe()
    },
    /** Revoke the session server-side; local state is cleared even if that fails. */
    async logout() {
      await call(identity.DELETE('/session')).catch(() => {})
      this.clear()
    },
  },
})
